import { useEffect, useMemo, useState } from "react";
import { BarChart3, Flame, Clock, Target } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";

const RANGES = [
  { id: 7, label: "7d" },
  { id: 14, label: "14d" },
  { id: 30, label: "30d" },
];

function fmtMinutes(m) {
  const hrs = Math.floor(m / 60);
  const mins = m % 60;
  if (hrs <= 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
}

function KpiCard({ label, value, sub, icon: Icon, testid }) {
  const text = String(value);
  return (
    <div
      className="group border border-border bg-card p-5 transition-colors hover:border-primary/60"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">// {label}</div>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div
        className="num-mono font-bold text-3xl mt-3 glitch"
        data-text={text}
      >
        {value}
      </div>
      {sub && <div className="font-mono text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Analytics() {
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(14);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/analytics/summary"),
      api.get(`/analytics/daily?days=${days}`),
    ])
      .then(([s, d]) => {
        if (cancelled) return;
        setSummary(s.data);
        setDaily(d.data || []);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  const chartData = useMemo(
    () => daily.map((d) => ({ ...d, label: d.date.slice(5) })),
    [daily]
  );

  const totalRangeMinutes = useMemo(
    () => daily.reduce((acc, d) => acc + (d.focus_minutes || 0), 0),
    [daily]
  );

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="section-label mb-3">analytics · focus_metrics</div>
        <h1 className="font-display font-bold text-4xl tracking-tight">Your Focus, Quantified</h1>
        <p className="mt-2 font-mono text-sm text-muted-foreground">{">"} pomodoro cycles you finished — broken down per day</p>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          <KpiCard
            label="today_focus"
            value={loading ? "—" : fmtMinutes(summary?.today_focus_minutes ?? 0)}
            sub={`${summary?.today_cycles ?? 0} cycle${(summary?.today_cycles ?? 0) === 1 ? "" : "s"}`}
            icon={Clock}
            testid="kpi-today"
          />
          <KpiCard
            label="streak"
            value={loading ? "—" : `${summary?.current_streak_days ?? 0}d`}
            sub="consecutive days"
            icon={Flame}
            testid="kpi-streak"
          />
          <KpiCard
            label="total_cycles"
            value={loading ? "—" : (summary?.total_cycles ?? 0)}
            sub="lifetime"
            icon={Target}
            testid="kpi-cycles"
          />
          <KpiCard
            label="total_focus"
            value={loading ? "—" : fmtMinutes(summary?.total_focus_minutes ?? 0)}
            sub="lifetime"
            icon={BarChart3}
            testid="kpi-focus"
          />
        </div>

        <div className="mt-10 border border-border bg-card">
          <div className="border-b border-border px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <BarChart3 className="h-3.5 w-3.5 text-primary" /> daily_focus_minutes
            </div>
            <div className="flex items-center gap-px bg-border" data-testid="range-selector">
              {RANGES.map((r) => (
                <Button
                  key={r.id}
                  size="sm"
                  variant={days === r.id ? "default" : "ghost"}
                  className="rounded-none font-mono text-xs uppercase tracking-widest"
                  onClick={() => setDays(r.id)}
                  data-testid={`range-${r.id}`}
                >
                  {r.label}
                </Button>
              ))}
            </div>
          </div>
          <div className="p-4">
            <div className="font-mono text-xs text-muted-foreground mb-4">
              {">"} range total: <span className="num-mono text-primary">{fmtMinutes(totalRangeMinutes)}</span>
            </div>
            <div className="h-72 w-full" data-testid="daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontFamily: "var(--font-mono, monospace)", fontSize: 10 }}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontFamily: "var(--font-mono, monospace)", fontSize: 10 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--primary) / 0.08)" }}
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 0,
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                    formatter={(value, name) => [
                      name === "focus_minutes" ? `${value} min` : value,
                      name === "focus_minutes" ? "focus" : name,
                    ]}
                  />
                  <Bar dataKey="focus_minutes" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
